/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { TextField } from "@mui/material";
import { useAuth } from "cms/auth/hooks/use-auth";
import { generateGuid, isStringEmpty } from "utils/empty-exists";
import { cloneObjectsArray } from "utils/array";
import { DocumentSelectorWrapper } from "cms/documents/document-selector/document-selector-wrapper";
import LabelledField from "components/labelled-field";
import { ComponentDocListItemContext } from "./component-document-list-context";
import debounce from "lodash.debounce";
import { Logger } from "cms/utils/log/logger";

const Document = () => {

    const { t } = useTranslation();
    const { token } = useAuth();
    const {
        list,
        item,
        index: idx,
        updateItems
    } = useContext(ComponentDocListItemContext);
    const {
        document: guid,
        title: itemTitle,
    } = item || {};
    const [document, setDocument] = useState();
    const titlePlaceholder = "Add document display title";
    const [title, setTitle] = useState(itemTitle);
    const [loaded, setLoaded] = useState(false);

    const loadDoc = async () => {
        if (guid && guid.length > 30) {
            const _doc = await SimpliCityAdminSDK.document.findOne(token, guid);
            setDocument(_doc);
            setTitle(_doc.title);
            return _doc;
        }
    };

    useEffect(() => {
        const load = async () => {
            Logger.debug("[document-list] re-load");
            if (item && Object.keys(item).length > 0) {
                setLoaded(true);
                await loadDoc(guid);
            }
        };
        if (!loaded) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list]);

    const onChooseDocument = (doc) => {
        if (!doc || isStringEmpty(doc)) {
            Logger.debug("no document selected");
            setDocument(undefined);
            return;
        }
        setDocument(doc);
        setTitle(doc?.title);
        const updatedDocs = cloneObjectsArray(list);
        const newdocitem = { ...updatedDocs[idx] };
        newdocitem.document = doc?.guid;
        newdocitem.title = doc?.title;
        updatedDocs[idx] = newdocitem;
        updateItems(updatedDocs);
        setLoaded(false);
    };
    const onChangeDocument = (doc) => {

        if (!doc || isStringEmpty(doc)) {
            Logger.debug("no document selected");
            return;
        }
        setDocument(doc);
        setTitle(doc.title);
        const updatedDocs = cloneObjectsArray(list);
        updatedDocs[idx].document = doc?.guid;
        updatedDocs[idx].title = doc?.title;
        updateItems(updatedDocs);
        setLoaded(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const changeDocTitle = useCallback(
        debounce((docTitle, list) => {
            const updatedDocs = cloneObjectsArray(list);
            updatedDocs[idx].title = docTitle;
            Logger.debug(`updating docs by title ${docTitle}`);
            updateItems(updatedDocs);
            // setLoaded(false)
        }),
        [updateItems]
    );
    const onChangeDocTitle = (event) => {
        const docTitle = event.target.value;
        setTitle(docTitle);
        changeDocTitle(docTitle,list);
    };

    const onRemoveDoc = () => {
        setDocument(undefined);
        setTitle("");
        const updatedDocs = cloneObjectsArray(list);
        updatedDocs[idx].guid = generateGuid();
        updatedDocs[idx].document = "";
        updatedDocs[idx].title = "";
        updateItems(updatedDocs);
    };

    return (
        <div style={{ flex: "1", border: "1px solid #CFCFCF", padding: "12px", borderRadius: "4px" }}>
            <DocumentSelectorWrapper
                document={document}
                setDocument={setDocument}
                onChooseDocumentHandler={onChooseDocument}
                onDocumentChange={onChangeDocument}
                onDocumentRemove={onRemoveDoc}
            />
            <LabelledField label={`${t("commons.displayTitle")}: `}>
                <TextField
                    id='documentTitle'
                    fullWidth={true}
                    placeholder={titlePlaceholder}
                    defaultValue={title}
                    onChange={onChangeDocTitle}
                />
            </LabelledField>
        </div>
    );
};

Document.displayName = "Document";
export { Document };
